import React, { useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { SEO } from '../components/SEO';
import { ArrowLeftIcon, ArrowRightIcon, ChevronRightIcon, InfoIcon, TargetIcon, ZapIcon } from 'lucide-react';
import { articles, getArticleBySlug, getArticlesByCluster } from '../data/seoBlogArticles';
export const SEOBlogPage: React.FC = () => {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const article = getArticleBySlug(slug || '');
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  if (!article) {
    return <Navigate to="/seo" replace data-id="element-1545" />;
  }
  const relatedClusterArticles = getArticlesByCluster(article.cluster).filter(a => a.slug !== article.slug).slice(0, 3);
  const renderContentBlock = (block: any, index: number) => {
    switch (block.type) {
      case 'heading':
        return <h2 key={index} className="text-2xl md:text-3xl font-bold text-[#111827] mt-12 mb-6" data-id="element-1546">
            {block.content}
          </h2>;
      case 'heading3':
        return <h3 key={index} className="text-xl md:text-2xl font-bold text-[#111827] mt-8 mb-4" data-id="element-1547">
            {block.content}
          </h3>;
      case 'paragraph':
        return <p key={index} className="text-lg text-[#4B5563] leading-relaxed mb-6" data-id="element-1548">
            {block.content}
          </p>;
      case 'list':
        return <ul key={index} className="space-y-3 mb-8 ml-6 list-disc text-lg text-[#4B5563]" data-id="element-1549">
            {block.items?.map((item: string, i: number) => <li key={i} className="pl-2 leading-relaxed" data-id="element-1550">
                {item}
              </li>)}
          </ul>;
      case 'callout':
        return <div key={index} className="bg-[#F7F8FA] border-l-4 border-[#007AFF] p-6 rounded-r-lg my-8" data-id="element-1551">
            <div className="flex items-start gap-4" data-id="element-1552">
              <InfoIcon className="w-6 h-6 text-[#007AFF] flex-shrink-0 mt-1" data-id="element-1553" />
              <p className="text-lg text-[#1B2B44] font-medium leading-relaxed m-0" data-id="element-1554">
                {block.content}
              </p>
            </div>
          </div>;
      case 'formula':
        return <div key={index} className="bg-[#1B2B44] text-white p-8 rounded-xl my-8 text-center shadow-lg" data-id="element-1555">
            <span className="text-sm text-white/60 uppercase tracking-wider font-semibold mb-3 block" data-id="element-1556">
              De Formule
            </span>
            <p className="text-xl md:text-2xl font-bold leading-relaxed" data-id="element-1557">
              {block.content}
            </p>
          </div>;
      default:
        return null;
    }
  };
  return <div className="w-full min-h-screen bg-white font-sans" data-id="element-1558">
      <SEO title={`${article.title} | Noord Digital SEO Kennisbank`} description={article.metaDescription} canonical={`https://noorddigital.nl/seo/blog/${article.slug}`} data-id="element-1559" />
      <Header data-id="element-1560" />

      {/* Breadcrumb & Hero */}
      <div className="bg-[#F7F8FA] pt-24 pb-16 border-b border-gray-200" data-id="element-1561">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-1562">
          <nav className="flex items-center text-sm text-[#6B7280] mb-8" data-id="element-1563">
            <Link to="/" className="hover:text-[#007AFF] transition-colors" data-id="element-1564">
              Home
            </Link>
            <ChevronRightIcon className="w-4 h-4 mx-2" data-id="element-1565" />
            <Link to="/seo" className="hover:text-[#007AFF] transition-colors" data-id="element-1566">
              SEO
            </Link>
            <ChevronRightIcon className="w-4 h-4 mx-2" data-id="element-1567" />
            <span className="text-[#111827] font-medium truncate" data-id="element-1568">
              {article.title}
            </span>
          </nav>

          <div className="max-w-3xl" data-id="element-1569">
            <span className="inline-block px-3 py-1 bg-blue-100 text-[#007AFF] text-sm font-semibold rounded-full mb-6" data-id="element-1570">
              {article.cluster}
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-[#111827] leading-tight mb-6" data-id="element-1571">
              {article.title}
            </h1>
            <p className="text-xl text-[#6B7280] leading-relaxed" data-id="element-1572">
              {article.metaDescription}
            </p>
          </div>
        </div>
      </div>

      {/* Main Content & Sidebar */}
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px] py-16" data-id="element-1573">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12" data-id="element-1574">
          {/* Article Content */}
          <div className="lg:col-span-8" data-id="element-1575">
            <article className="prose prose-lg max-w-none prose-headings:text-[#111827] prose-p:text-[#4B5563] prose-a:text-[#007AFF]" data-id="element-1576">
              {article.content.map((block, index) => renderContentBlock(block, index))}
            </article>

            {/* Author / Bio block */}
            <div className="mt-16 pt-8 border-t border-gray-200 flex items-center gap-6" data-id="element-1577">
              <img src="https://cdn.magicpatterns.com/uploads/fv5nFyNt1SPvWUkxd2YKYJ/Norden_portret_300dpi_2.png" alt="Norden" className="w-16 h-16 rounded-full bg-[#F7F8FA] border border-gray-200" data-id="element-1578" />
              <div data-id="element-1579">
                <h4 className="font-bold text-[#111827]" data-id="element-1580">Norden</h4>
                <p className="text-[#6B7280] text-sm" data-id="element-1581">
                  SEO Gids bij Noord Digital
                </p>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-4" data-id="element-1582">
            <div className="sticky top-24 space-y-8" data-id="element-1583">
              {/* Back to SEO */}
              <Link to="/seo" className="flex items-center justify-center gap-2 w-full py-4 px-6 bg-[#F7F8FA] hover:bg-gray-100 text-[#1B2B44] font-semibold rounded-xl transition-colors border border-gray-200" data-id="element-1584">
                <ArrowLeftIcon className="w-5 h-5" data-id="element-1585" />
                Terug naar SEO overzicht
              </Link>

              {/* CTA Card */}
              <div className="bg-gradient-to-br from-[#007AFF] to-[#1B2B44] rounded-2xl p-8 text-white shadow-xl" data-id="element-1586">
                <h3 className="text-xl font-bold mb-3" data-id="element-1587">
                  SEO als systeem inzetten?
                </h3>
                <p className="text-white/80 text-sm leading-relaxed mb-6" data-id="element-1588">
                  Stop met losse flodders. Ontdek waar jouw kansen liggen voor
                  structurele groei in Google.
                </p>
                <a href="/prijsscan" className="block w-full py-3 px-4 bg-white text-[#007AFF] text-center font-bold rounded-lg hover:bg-gray-50 transition-colors" data-id="element-1589">
                  Start de SEO scan
                </a>
              </div>

              {/* Related Articles */}
              {article.relatedArticles && article.relatedArticles.length > 0 && <div className="bg-white border border-gray-200 rounded-2xl p-6" data-id="element-1590">
                    <h3 className="text-lg font-bold text-[#111827] mb-4 flex items-center gap-2" data-id="element-1591">
                      <TargetIcon className="w-5 h-5 text-[#007AFF]" data-id="element-1592" />
                      Gerelateerd
                    </h3>
                    <ul className="space-y-3" data-id="element-1593">
                      {article.relatedArticles.map((relSlug, idx) => {
                  const relArticle = getArticleBySlug(relSlug);
                  if (!relArticle) return null;
                  return <li key={idx} data-id="element-1594">
                            <Link to={`/seo/blog/${relArticle.slug}`} className="text-sm text-[#4B5563] hover:text-[#007AFF] hover:underline flex items-start gap-2 leading-snug" data-id="element-1595">
                              <ChevronRightIcon className="w-4 h-4 mt-0.5 flex-shrink-0 text-gray-400" data-id="element-1596" />
                              {relArticle.title}
                            </Link>
                          </li>;
                })}
                    </ul>
                  </div>}
            </div>
          </div>
        </div>
      </div>

      {/* More from this cluster */}
      {relatedClusterArticles.length > 0 && <div className="bg-[#F7F8FA] py-20 border-t border-gray-200" data-id="element-1597">
          <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-1598">
            <h2 className="text-2xl md:text-3xl font-bold text-[#111827] mb-10 text-center" data-id="element-1599">
              Meer over {article.cluster}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1600">
              {relatedClusterArticles.map((relArticle, idx) => <Link key={idx} to={`/seo/blog/${relArticle.slug}`} className="bg-white rounded-xl p-6 border border-gray-200 hover:shadow-lg hover:border-[#007AFF]/30 transition-all group" data-id="element-1601">
                  <h3 className="text-lg font-bold text-[#1B2B44] mb-3 group-hover:text-[#007AFF] transition-colors" data-id="element-1602">
                    {relArticle.title}
                  </h3>
                  <p className="text-[#6B7280] text-sm leading-relaxed mb-4 line-clamp-3" data-id="element-1603">
                    {relArticle.metaDescription}
                  </p>
                  <span className="text-[#007AFF] text-sm font-semibold flex items-center gap-1" data-id="element-1604">
                    Lees artikel <ArrowRightIcon className="w-4 h-4" data-id="element-1605" />
                  </span>
                </Link>)}
            </div>
          </div>
        </div>}

      <Footer data-id="element-1606" />
    </div>;
};